import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@platform/ui'
import { ArrowUpRight, Check, Sparkles } from 'lucide-react'
import { registrar } from '@/lib/demo/telemetry'
import { platform } from '@/lib/platform'

/**
 * Intención de compra — el evento más pesado del catálogo (60 puntos).
 *
 * POR QUÉ PESA TANTO: los otros siete eventos miden que alguien recorre el
 * producto. Este mide que decidió. Un prospecto puede simular diez compras por
 * curiosidad y seguir sin querer nada; el que pulsa aquí está diciendo, con
 * todas las letras, que quiere la plataforma para sí.
 *
 * Aun así no basta solo: 5 de apertura más 60 de intención dan 65, y el umbral
 * de caliente está en 70. Es deliberado y está probado —ver la prueba "la
 * intención de compra deja al prospecto a un paso"—. Para pulsar este botón hay
 * que estar dentro de la plataforma, y quien está dentro ya miró algo, así que
 * en la práctica cruza igual. El margen existe para que un solo clic accidental
 * no encienda la bandeja comercial.
 *
 * DOS DECISIONES QUE PARECEN DETALLES Y NO LO SON:
 *
 * 1. La señal se emite en el clic, ANTES de abrir el diálogo y antes de
 *    cualquier navegación. `registrar` sale por `sendBeacon`, que sobrevive a
 *    que la pestaña se cierre — y este es justo el evento que más se pierde si
 *    se emite tarde, porque quien lo pulsa suele irse a escribir un correo.
 *
 * 2. La deduplicación es de por vida, no diaria. Que alguien vuelva tres veces
 *    a pulsarlo no lo hace tres veces más interesante; ya está marcado y quien
 *    atiende la bandeja ya lo vio.
 */
export function ActivarPlataforma() {
  const [abierto, setAbierto] = useState(false)
  const marca = platform.brand?.productName ?? 'la plataforma'
  const correo = platform.brand?.supportEmail

  function pulsar() {
    registrar('checkout_intent', { origen: 'conmutador-flotante' })
    setAbierto(true)
  }

  return (
    <>
      <Button
        onClick={pulsar}
        size="lg"
        variant="outline"
        className="gap-2 rounded-full border-primary/30 bg-background/80 shadow-md backdrop-blur hover:bg-primary/5"
      >
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="hidden sm:inline">Activá tu plataforma</span>
        <span className="sm:hidden">Activar</span>
      </Button>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Tu plataforma, con tu marca</DialogTitle>
            <DialogDescription>
              Lo que acabás de recorrer se entrega con tu identidad, tu dominio y tus activos. No es
              un desarrollo desde cero: es esto, configurado para vos.
            </DialogDescription>
          </DialogHeader>

          <ul className="grid gap-3 py-2 text-sm">
            {[
              'Tu marca y tu dominio, de punta a punta.',
              'Emisión de activos y control de la colocación desde tu propio panel.',
              'Verificación de identidad, pagos y reparto de rendimientos ya integrados.',
              'Puesta en marcha acompañada, no un manual y suerte.',
            ].map((linea) => (
              <li key={linea} className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="text-muted-foreground">{linea}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row">
            {correo ? (
              <Button asChild className="gap-2 sm:flex-1">
                <a
                  href={`mailto:${correo}?subject=${encodeURIComponent(`Quiero activar ${marca}`)}`}
                >
                  Hablemos
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setAbierto(false)} className="sm:w-auto">
              Sigo mirando
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
